import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Container, Image, ImageBox} from '../model/layout.model';
import {DocumentModel} from '../model/document.model';
import {EventBusService} from './event-bus.service';
import {EventBusTypes} from '../model/constants';
import {GrafikUmfang} from '../model/event-bus.model';

/** Eine Quelle der Prognose-Karte: die Aussichtentag-Karte und ihre Spalte. */
export interface PrognoseQuelle {
  /** `containerId` der Karte 3 der Zeile "<n>. AUSSICHTENTAG: ganz". */
  quellId: string;
  /** Tag der Datei (= Spalte "Tag 2" … "Tag 4"): 1. Aussichtentag → 2. */
  tag: number;
  /** Nummer des Aussichtentags (1 … 3), für Meldungen. */
  aussichtentag: number;
}

/**
 * "gespeicherte Daten laden" (November 2026): der Knopf in der Leiste des Lokalzeit-Reiters
 * "Tendenzen/Prognosen" (neben "MSWR-MOS laden") kopiert die TV-relevanten Werte der drei Zeilen
 * "1./2./3. AUSSICHTENTAG: ganz" (Karte 3 "alle Regionen") in die Spalten Tag 2/3/4 der
 * Prognose-Karte der Stadt (`wdr.lokalzeit.<stadt>.prognose.karte1`).
 *
 * Was wandert, folgt den MOS-Schlüsseln der Prognose-Spalte (`T_Tag0N`, `P_Tag0N`,
 * `Wvel_Tag0N`, `NProb_Tag0N`, `Shrs_Tag0N`; N = Aussichtentag + 1, wie `Tag02` in der Datei):
 * die drei TV-Boxen Wind km/h / Regen % / Sonne h über ihren **gleichen `dataKey`**, und die
 * Kombi-Box des Hauptorts (die einzige `tempicon`-Box der Karte – Tageshöchstwert und
 * Tagessymbol) in `T_Tag0N` bzw. `P_Tag0N`. Die Nacht-Werte der Spalte (`T_Nacht0N`,
 * `P_Nacht0N`) haben auf der Aussichtentag-Karte keine Quelle und bleiben stehen.
 *
 * Gefüllte Quellwerte **überschreiben** die Prognose (geladen oder eingetippt); eine leere
 * Quell-Box lässt ihre Ziel-Box unangetastet. Geschrieben wird in `image.values` des
 * Dokumentbaums, eine schon gerenderte Prognose-Karte erfährt davon über
 * `GRAFIK_WERTE_GEAENDERT_EVENT` (wie bei "Höchstwerte übernehmen").
 */
@Injectable({providedIn: 'root'})
export class PrognoseUebernahmeService {

  /** `wdr.lokalzeit.<stadt>.prognose.karte1` – die Prognose-Karte einer Stadt ("Lokalzeit am Samstag" hat keine). */
  private static readonly PROGNOSE = /^wdr\.lokalzeit\.([a-z-]+)\.prognose\.karte1$/;
  private static readonly AUSSICHTENTAGE = [1, 2, 3];
  private static readonly ICON_SUFFIX = '.icon';
  private static readonly HAUPTORT_TYP = 'tempicon';

  constructor(private eventBus: EventBusService, private snackBar: MatSnackBar) {
  }

  /**
   * Die drei Quellen einer Prognose-Karte – oder null, wenn die Karte keine Prognose-Karte ist.
   * `wdr.lokalzeit.koeln.prognose.karte1` → `wdr.lokalzeit.koeln.1-aussichtentag-ganz.karte3` (Tag 2) …
   */
  static quellenFuer(containerId: string | null | undefined): PrognoseQuelle[] | null {
    const treffer = containerId?.match(PrognoseUebernahmeService.PROGNOSE);
    if (!treffer) {
      return null;
    }
    return PrognoseUebernahmeService.AUSSICHTENTAGE.map(tag => ({
      quellId: `wdr.lokalzeit.${treffer[1]}.${tag}-aussichtentag-ganz.karte3`, tag: tag + 1, aussichtentag: tag
    }));
  }

  /**
   * Überträgt die TV-Werte der drei Aussichtentag-Karten (gesucht im Dokument) in die Spalten der
   * Prognose-Karten des Umfangs und meldet das Ergebnis per Snackbar.
   *
   * @returns Zahl der übernommenen Werte
   */
  uebernehmen(document: DocumentModel | null | undefined, kartenIds: string[]): number {
    let uebernommen = 0;
    const geaendert: string[] = [];
    const jeSpalte: string[] = [];
    let prognosen = 0;
    for (const prognoseId of kartenIds) {
      const quellen = PrognoseUebernahmeService.quellenFuer(prognoseId);
      const prognose = quellen ? PrognoseUebernahmeService.finde(document?.tabs, prognoseId) : null;
      if (!quellen || !prognose) {
        continue;
      }
      prognosen++;
      for (const quelle of quellen) {
        const karte = PrognoseUebernahmeService.finde(document?.tabs, quelle.quellId);
        if (!karte) {
          console.warn(`WARN: "gespeicherte Daten laden": Karte ${quelle.quellId} nicht im Dokument`);
          continue;
        }
        const kopiert = PrognoseUebernahmeService.kopiere(karte, prognose, quelle.tag);
        jeSpalte.push(`Tag ${quelle.tag}: ${kopiert}`);
        if (kopiert > 0) {
          uebernommen += kopiert;
          if (!geaendert.includes(prognose.containerId)) {
            geaendert.push(prognose.containerId);
          }
        }
      }
    }
    if (geaendert.length > 0) {
      this.eventBus.emit(EventBusTypes.GRAFIK_WERTE_GEAENDERT_EVENT.valueOf(), new GrafikUmfang(geaendert));
      this.snackBar.open(`Gespeicherte Daten geladen: ${uebernommen} Werte aus den Aussichtentagen in die Prognose `
        + `übernommen (${jeSpalte.join(', ')}).`, 'OK', {duration: 6000, panelClass: 'hinweis-erfolg'});
    } else if (prognosen === 0) {
      this.snackBar.open('Keine Daten geladen: in diesem Umfang liegt keine Prognose-Karte.', 'OK',
        {duration: 7000, panelClass: 'hinweis-fehler'});
    } else {
      this.snackBar.open('Keine Daten geladen: auf den Karten der Aussichtentage (Zeile "ganz") sind keine '
        + 'TV-Werte eingetragen (erst "MSWR-MOS laden" oder Werte eintippen).', 'OK',
        {duration: 7000, panelClass: 'hinweis-fehler'});
    }
    return uebernommen;
  }

  /**
   * Die Werte einer Quell-Karte in die Spalte `tag` der Prognose: Quell-Box → Ziel-`dataKey`
   * nach {@link zielSchluessel}, Ziel-Box über diesen `dataKey`. Überschreibt gefüllte Werte,
   * lässt Ziel-Boxen mit leerer Quelle stehen.
   */
  private static kopiere(quelle: Image, prognose: Image, tag: number): number {
    const nachSchluessel = new Map<string, ImageBox>();
    for (const box of prognose.boxes ?? []) {
      if (box.dataKey) {
        nachSchluessel.set(box.dataKey, box);
      }
    }
    const werte = {...(prognose.values ?? {})};
    let kopiert = 0;
    for (const box of quelle.boxes ?? []) {
      for (const [wertSchluessel, zielSchluessel] of PrognoseUebernahmeService.zielSchluessel(box, tag)) {
        const zielBox = nachSchluessel.get(zielSchluessel);
        const wert = quelle.values?.[wertSchluessel];
        if (!zielBox || wert === undefined || wert === null || wert === '') {
          continue;
        }
        werte[zielBox.id] = wert;
        kopiert++;
      }
    }
    if (kopiert > 0) {
      prognose.values = werte;
    }
    return kopiert;
  }

  /**
   * Welche Werte einer Quell-Box wohin gehen: `[Schlüssel im Wert-Cache der Quelle, dataKey der
   * Ziel-Box]`. TV-Boxen tragen den Schlüssel selbst (`Wvel_Tag02` …), die Kombi-Box des
   * Hauptorts liefert Temperatur und Symbol für `T_Tag0N` und `P_Tag0N`.
   */
  private static zielSchluessel(box: ImageBox, tag: number): [string, string][] {
    if (box.dataKey) {
      return [[box.id, box.dataKey]];
    }
    if (box.type === PrognoseUebernahmeService.HAUPTORT_TYP) {
      const nn = String(tag).padStart(2, '0');
      return [[box.id, `T_Tag${nn}`], [box.id + PrognoseUebernahmeService.ICON_SUFFIX, `P_Tag${nn}`]];
    }
    return [];
  }

  /** Sucht ein Bild im Dokument über seine containerId. */
  private static finde(container: Container[] | null | undefined, containerId: string): Image | null {
    for (const kind of container ?? []) {
      if (kind.containerType === 'Image' && kind.containerId === containerId) {
        return kind as Image;
      }
      const gefunden = PrognoseUebernahmeService.finde(kind.containerList, containerId);
      if (gefunden) {
        return gefunden;
      }
    }
    return null;
  }
}
